// v2/components/CatalogFilters.js — urunler sayfasi: sekmeler + arac / kategori filtreleri
// Secenekler ve sayilar /api/v2/catalog/facets/ ucundan gelir.
import React, { useMemo } from 'react';
import { X, LayoutGrid, History, Tag } from 'lucide-react';
import { Strip } from './FilterStrips';
import BrandLogo from './BrandLogo';
import Select from './Select';
import { categoryStyle } from '../constants/catalogVisuals';
import { formatNumber } from '../utils/format';
import fs from './FilterStrips.module.css';
import styles from './CatalogFilters.module.css';

const TABS = [
  { id: 'all', label: 'Tüm Ürünler', Icon: LayoutGrid },
  { id: 'frequent', label: 'Sık Aldıklarınız', Icon: History },
  { id: 'campaign', label: 'Kampanyalı', Icon: Tag },
];
const SERIT_MARKA = 40;
const collator = new Intl.Collator('tr', { numeric: true, sensitivity: 'base' });
const dizi = (v) => (Array.isArray(v) ? v : []);

function Secim({ label, value, onChange, disabled, bosEtiket, secenekler }) {
  const varMi = !value || secenekler.some((s) => s.value === value);
  const liste = varMi ? secenekler : [{ value, label: value }, ...secenekler];
  return (
    <div className={`${styles.field} ${value ? styles.fieldOn : ''}`}>
      <span>{label}</span>
      <Select
        block value={value} disabled={disabled} options={liste}
        placeholder={bosEtiket} on={Boolean(value)} ariaLabel={label}
        onChange={(v) => onChange(v)}
      />
    </div>
  );
}

export default function CatalogFilters({
  filters: f, facets, total = 0, loading = false, onChange, onClearAll,
}) {
  const brands = dizi(facets && facets.brands);
  const cats = dizi(facets && facets.categories);
  const subs = dizi(facets && facets.subcategories);
  const segs = dizi(facets && facets.segments);
  const engs = dizi(facets && facets.engines);

  const serit = useMemo(() => {
    const ust = brands.slice(0, SERIT_MARKA);
    if (f.brand && !ust.some((b) => b.id === f.brand)) {
      const s = brands.find((b) => b.id === f.brand);
      if (s) ust.unshift(s);
    }
    return ust;
  }, [brands, f.brand]);

  const digerMarkalar = useMemo(
    () => brands
      .filter((b) => !serit.some((s) => s.id === b.id))
      .sort((a, b) => collator.compare(a.name, b.name)),
    [brands, serit]
  );

  const modeller = useMemo(
    () => [...dizi(facets && facets.models)].sort((a, b) => collator.compare(a.name, b.name)),
    [facets]
  );

  const markaAdi = f.brand ? ((brands.find((b) => b.id === f.brand) || {}).name || f.brand) : '';

  const markaSec = (id) => onChange(id === f.brand ? { brand: '', model: '' } : { brand: id, model: '' });
  const kategoriSec = (ad) => onChange(ad === f.category ? { category: '', sub: '' } : { category: ad, sub: '' });

  const secili = [
    f.brand && { key: 'brand', label: markaAdi, clear: { brand: '', model: '' } },
    f.model && { key: 'model', label: f.model, clear: { model: '' } },
    f.segment && { key: 'segment', label: f.segment, clear: { segment: '' } },
    f.engine && { key: 'engine', label: f.engine, clear: { engine: '' } },
    f.category && { key: 'category', label: f.category, clear: { category: '', sub: '' } },
    f.sub && { key: 'sub', label: f.sub, clear: { sub: '' } },
  ].filter(Boolean);

  return (
    <section className={fs.wrap} aria-label="Ürün filtreleri">
      <div className={styles.tabsRow}>
        <div className={styles.tabs} role="tablist" aria-label="Ürün listesi">
          {TABS.map(({ id, label, Icon }) => {
            const on = f.tab === id;
            return (
              <button
                key={id} type="button" role="tab" aria-selected={on}
                className={`${styles.tab} ${on ? styles.tabOn : ''}`}
                onClick={() => onChange({ tab: id })}
              >
                <Icon size={15} />{label}
              </button>
            );
          })}
        </div>
        <span className={styles.total}>
          <b>{formatNumber(total)}</b> ürün
        </span>
      </div>

      <Strip label="Araç Markası" count={serit.length}>
        {serit.map((b, i) => {
          const on = b.id === f.brand;
          return (
            <button
              key={b.id} type="button"
              className={`${fs.brandCard} ${on ? fs.cardOn : ''}`}
              style={{ animationDelay: `${Math.min(i * 18, 260)}ms` }}
              onClick={() => markaSec(b.id)}
              aria-pressed={on}
              title={`${b.name} · ${formatNumber(b.count)} ürün`}
            >
              <BrandLogo id={b.id} name={b.name} size="md" />
              <span className={fs.brandName}>{b.name}</span>
              <span className={styles.cnt}>{formatNumber(b.count)}</span>
            </button>
          );
        })}
        {!serit.length && (
          <span className={styles.stripEmpty}>
            {loading ? 'Yükleniyor…' : 'Bu filtrelerde araç bilgisi olan ürün yok'}
          </span>
        )}
      </Strip>

      <div className={fs.strip}>
        <span className={fs.stripLabel}>Araç Detayı</span>
        <div className={styles.selects}>
          <Secim
            label="Diğer Markalar" value=""
            disabled={!digerMarkalar.length}
            bosEtiket={digerMarkalar.length ? `Seçiniz (${digerMarkalar.length})` : 'Yok'}
            secenekler={digerMarkalar.map((b) => ({
              value: b.id, label: `${b.name} (${formatNumber(b.count)})`,
            }))}
            onChange={(v) => v && onChange({ brand: v, model: '' })}
          />
          <Secim
            label="Model" value={f.model}
            disabled={!f.brand || (!modeller.length && !f.model)}
            bosEtiket={f.brand ? `Tüm modeller (${modeller.length})` : 'Önce marka seçin'}
            secenekler={modeller.map((m) => ({
              value: m.name, label: `${m.name} (${formatNumber(m.count)})`,
            }))}
            onChange={(v) => onChange({ model: v })}
          />
          <Secim
            label="Araç Tipi" value={f.segment} bosEtiket="Tümü"
            secenekler={segs.map((s) => ({ value: s.name, label: `${s.name} (${formatNumber(s.count)})` }))}
            onChange={(v) => onChange({ segment: v })}
          />
          <Secim
            label="Yakıt" value={f.engine} bosEtiket="Tümü"
            secenekler={engs.map((s) => ({ value: s.name, label: `${s.name} (${formatNumber(s.count)})` }))}
            onChange={(v) => onChange({ engine: v })}
          />
        </div>
      </div>

      <Strip label="Kategori" count={cats.length}>
        {cats.map((c, i) => {
          const { Icon, tint } = categoryStyle(c.name);
          const on = c.name === f.category;
          return (
            <button
              key={c.name} type="button"
              className={`${fs.catCard} ${on ? fs.cardOn : ''}`}
              style={{ animationDelay: `${Math.min(i * 22, 260)}ms` }}
              onClick={() => kategoriSec(c.name)}
              aria-pressed={on}
            >
              <span className={fs.catIcon} style={{ color: tint }}>
                <Icon size={19} strokeWidth={1.7} />
              </span>
              <span className={fs.catName}>{c.name}</span>
              <span className={styles.cnt}>{formatNumber(c.count)}</span>
            </button>
          );
        })}
        {!cats.length && (
          <span className={styles.stripEmpty}>{loading ? 'Yükleniyor…' : 'Kategori yok'}</span>
        )}
      </Strip>

      {f.category && subs.length > 0 && (
        <div className={fs.strip}>
          <span className={fs.stripLabel}>Alt Kategori</span>
          <div className={styles.subChips}>
            {subs.map((s) => {
              const on = s.name === f.sub;
              return (
                <button
                  key={s.name} type="button" aria-pressed={on}
                  className={`${styles.subChip} ${on ? styles.subChipOn : ''}`}
                  onClick={() => onChange({ sub: on ? '' : s.name })}
                >
                  {s.name}
                  <span className={styles.cnt}>{formatNumber(s.count)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {secili.length > 0 && (
        <div className={fs.chosen}>
          <span className={fs.chosenLabel}>Seçili:</span>
          {secili.map((x) => (
            <button key={x.key} type="button" className={fs.chosenChip}
                    onClick={() => onChange(x.clear)}>
              {x.label} <X size={12} />
            </button>
          ))}
          <button type="button" className={fs.chosenClear} onClick={onClearAll}>
            Tümünü kaldır
          </button>
        </div>
      )}
    </section>
  );
}
